import {
  createCustomConfig,
  getCustomConfigByKey,
  updateCustomConfigById,
} from "../Reports/Components/utils";
import {
  COLUMN_CUSTOMIZATION_VERSION,
  createDefaultCustomView,
  emptyColumnCustomizationValue,
  getColumnCustomizationKey,
  getDefaultCustomSeedKeys,
  normalizePageConfig,
} from "./columnCustomizationUtils";

const getConfigId = (config) => config?.id ?? config?._id ?? null;

export const getColumnCustomizationConfig = async (clientId, platform) => {
  const key = getColumnCustomizationKey(platform);
  const config = await getCustomConfigByKey(clientId, key);
  const value = config?.value ?? emptyColumnCustomizationValue(platform);

  return {
    key,
    configId: getConfigId(config),
    value: {
      platform: value?.platform ?? platform,
      version: value?.version ?? COLUMN_CUSTOMIZATION_VERSION,
      pages: value?.pages && typeof value.pages === "object" ? value.pages : {},
    },
  };
};

export const getPageColumnCustomization = (
  configValue,
  pageId,
  columns = [],
  seedOptions = {}
) => {
  const pageConfig = configValue?.pages?.[pageId] ?? null;
  return normalizePageConfig(pageConfig, columns, seedOptions);
};

export const savePageColumnCustomization = async ({
  clientId,
  platform,
  pageId,
  pageConfig,
  existingConfig = null,
  columns = [],
}) => {
  const key = getColumnCustomizationKey(platform);
  const current =
    existingConfig ?? (await getColumnCustomizationConfig(clientId, platform));

  const normalizedPage = normalizePageConfig(pageConfig, columns);
  if (!normalizedPage.views.some((view) => view.id === "DEFAULT_CUSTOM")) {
    normalizedPage.views.unshift(
      createDefaultCustomView(getDefaultCustomSeedKeys(columns, normalizedPage))
    );
  }

  const nextValue = {
    platform,
    version: COLUMN_CUSTOMIZATION_VERSION,
    pages: {
      ...(current?.value?.pages || {}),
      [pageId]: normalizedPage,
    },
  };

  const payload = { key, value: nextValue };

  if (current?.configId) {
    await updateCustomConfigById(clientId, current.configId, payload);
    return {
      key,
      configId: current.configId,
      value: nextValue,
    };
  }

  await createCustomConfig(clientId, payload);
  const refreshed = await getColumnCustomizationConfig(clientId, platform);
  return {
    key,
    configId: refreshed?.configId ?? null,
    value: refreshed?.value ?? nextValue,
  };
};

export const upsertCustomViewOnPage = async ({
  clientId,
  platform,
  pageId,
  view,
  activeViewId,
  existingConfig = null,
  columns = [],
}) => {
  const current =
    existingConfig ?? (await getColumnCustomizationConfig(clientId, platform));
  const pageConfig = getPageColumnCustomization(
    current.value,
    pageId,
    columns
  );

  const views = [...pageConfig.views];
  const index = views.findIndex((item) => item.id === view.id);
  const nextView = {
    ...view,
    updatedAt: new Date().toISOString(),
  };

  if (index >= 0) {
    views[index] = { ...views[index], ...nextView };
  } else {
    views.push(nextView);
  }

  return savePageColumnCustomization({
    clientId,
    platform,
    pageId,
    pageConfig: {
      activeViewId: activeViewId ?? view.id,
      views,
    },
    existingConfig: current,
    columns,
  });
};

export const deleteCustomViewOnPage = async ({
  clientId,
  platform,
  pageId,
  viewId,
  existingConfig = null,
  columns = [],
  fallbackActiveViewId = null,
}) => {
  if (viewId === "DEFAULT_CUSTOM") {
    throw new Error("Default Custom View cannot be deleted");
  }

  const current =
    existingConfig ?? (await getColumnCustomizationConfig(clientId, platform));
  const pageConfig = getPageColumnCustomization(
    current.value,
    pageId,
    columns
  );

  const views = pageConfig.views.filter((view) => view.id !== viewId);
  const nextActiveViewId =
    pageConfig.activeViewId === viewId
      ? fallbackActiveViewId
      : pageConfig.activeViewId;

  return savePageColumnCustomization({
    clientId,
    platform,
    pageId,
    pageConfig: {
      activeViewId: nextActiveViewId,
      views,
    },
    existingConfig: current,
    columns,
  });
};

export const renameCustomViewOnPage = async ({
  clientId,
  platform,
  pageId,
  viewId,
  name,
  existingConfig = null,
  columns = [],
}) => {
  if (viewId === "DEFAULT_CUSTOM") {
    throw new Error("Default Custom View cannot be renamed");
  }

  const current =
    existingConfig ?? (await getColumnCustomizationConfig(clientId, platform));
  const pageConfig = getPageColumnCustomization(
    current.value,
    pageId,
    columns
  );

  const views = pageConfig.views.map((view) =>
    view.id === viewId
      ? { ...view, name: name?.trim() || view.name, updatedAt: new Date().toISOString() }
      : view
  );

  return savePageColumnCustomization({
    clientId,
    platform,
    pageId,
    pageConfig: {
      activeViewId: pageConfig.activeViewId,
      views,
    },
    existingConfig: current,
    columns,
  });
};
